import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { CheckboxGroup } from '@sorare/core/src/atoms/inputs/CheckboxGroup';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useGenerateDialogPath } from '@sorare/core/src/hooks/navigation/useGenerateDialogPath';
import { useMarkNotificationsAsRead } from '@sorare/core/src/hooks/notification/useMarkNotificationsAsRead';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { isType } from '@sorare/core/src/lib/gql';
import {
  getFormattedSport,
  getSportOrSharedFromSport,
} from '@sorare/core/src/lib/sports';

import { DumbNotification } from '../DumbNotification';
import { AnnouncementNotification } from '../Notification/AnnouncementNotification';
import type { AnnouncementNotification_announcementNotification } from '../Notification/AnnouncementNotification/__generated__/index.graphql';
import { FiltersContainer, ItemsContainer, Root } from '../ui';
import type {
  GetNewsQuery,
  GetNewsQueryVariables,
  GetNotificationsNewsQuery,
  GetNotificationsNewsQueryVariables,
} from './__generated__/index.graphql';

const GET_NEWS_QUERY = gql`
  query GetNewsQuery($cursor: String, $sports: [Sport!]) {
    announcements(after: $cursor, sports: $sports, first: 10) {
      nodes {
        id
        title
        createdAt
        sport
        url
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
` as TypedDocumentNode<GetNewsQuery, GetNewsQueryVariables>;

const GET_NOTIFICATIONS_NEWS_QUERY = gql`
  query GetNotificationsNewsQuery(
    $cursor: String
    $categories: [NotificationCategoryInput!]
    $sports: [Sport!]
  ) {
    currentUser {
      slug
      notifications: anyNotifications(
        after: $cursor
        categories: $categories
        sports: $sports
      ) {
        nodes {
          id
          ...AnnouncementNotification_announcementNotification
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${AnnouncementNotification.fragments.announcementNotification}
` as TypedDocumentNode<
  GetNotificationsNewsQuery,
  GetNotificationsNewsQueryVariables
>;

const sports = Object.values(Sport);

export const News = () => {
  const { currentUser } = useCurrentUserContext();
  const markNotificationsAsRead = useMarkNotificationsAsRead();
  const generateDialogPath = useGenerateDialogPath();
  const { sport } = useSportContext();
  const [selectedSports, setSelectedSports] = useState<Sport[]>([sport]);

  const {
    data: dataNews,
    loading: loadingNews,
    loadMore: loadMoreNews,
  } = usePaginatedQuery(GET_NEWS_QUERY, {
    connection: 'AnnouncementConnection',
    variables: {
      sports: selectedSports,
    },
    skip: !!currentUser,
  });

  const {
    data: dataNotificationsNews,
    loading: loadingNotificationsNews,
    loadMore: loadMoreNotificationsNews,
  } = usePaginatedQuery(GET_NOTIFICATIONS_NEWS_QUERY, {
    variables: {
      sports: selectedSports,
      categories: [
        {
          name: 'announcement',
          type: 'AnnouncementNotification',
        },
      ],
    },
    connection: 'NotificationInterfaceConnection',
    skip: !currentUser,
  });

  const hasNextPage = useMemo(
    () =>
      Boolean(
        currentUser
          ? dataNotificationsNews?.currentUser?.notifications.pageInfo
              .hasNextPage
          : dataNews?.announcements?.pageInfo?.hasNextPage
      ),
    [
      currentUser,
      dataNews?.announcements?.pageInfo?.hasNextPage,
      dataNotificationsNews?.currentUser?.notifications.pageInfo.hasNextPage,
    ]
  );

  const loading = useMemo(
    () => (currentUser ? loadingNotificationsNews : loadingNews),
    [currentUser, loadingNews, loadingNotificationsNews]
  );

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      const loadMore = currentUser ? loadMoreNotificationsNews : loadMoreNews;
      loadMore(false, {
        cursor: dataNews?.announcements?.pageInfo.endCursor,
      });
    },
    hasNextPage,
    loading
  );

  const notificationsNews = useMemo(() => {
    if (!currentUser) return [];

    return dataNotificationsNews?.currentUser?.notifications.nodes
      ?.filter(Boolean)
      .filter(notification =>
        isType(notification, 'AnnouncementNotification')
      ) as AnnouncementNotification_announcementNotification[] | undefined;
  }, [currentUser, dataNotificationsNews?.currentUser?.notifications.nodes]);

  useEffect(() => {
    if (notificationsNews?.length) {
      markNotificationsAsRead(notificationsNews);
    }
  }, [notificationsNews, markNotificationsAsRead]);

  return (
    <Root>
      <FiltersContainer>
        <FilterInDropdown
          buttonLabel={
            <FormattedMessage
              id="Activity.Notifications.sports"
              defaultMessage="{nbSports, plural, =0 {All sports} other {Sports (#)}}"
              values={{
                nbSports: selectedSports.length,
              }}
            />
          }
          filterSelected={!!selectedSports.length}
          onClearFilter={() => setSelectedSports([])}
        >
          <CheckboxGroup
            options={sports.map(s => ({
              value: s,
              label: <LabelM>{getFormattedSport(s)}</LabelM>,
            }))}
            selectedValues={selectedSports}
            onChange={setSelectedSports}
          />
        </FilterInDropdown>
      </FiltersContainer>
      <ItemsContainer>
        {currentUser ? (
          <>
            {notificationsNews?.map(notification => (
              <AnnouncementNotification
                key={notification.announcement.id}
                notification={notification}
              />
            ))}
          </>
        ) : (
          <>
            {dataNews?.announcements?.nodes?.map(
              announcement =>
                announcement && (
                  <DumbNotification
                    name="news"
                    key={announcement.id}
                    createdAt={announcement.createdAt}
                    title={announcement.title}
                    sport={getSportOrSharedFromSport(announcement.sport)}
                    link={
                      announcement.url ||
                      generateDialogPath(
                        DialogKey.news,
                        idFromObject(announcement.id)
                      )
                    }
                  />
                )
            )}
          </>
        )}
        <InfiniteScrollLoader />
      </ItemsContainer>
    </Root>
  );
};
